import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import "@26lights/orcha/dist/orcha.css";

import enUK from "@/locales/templates/en-UK.json";
import { useMutation, useQuery } from "@vue/apollo-composable";
import { GET_TEMPLATES } from "@/graphql/orcha/templates/queries";
import { logErrorMessages } from "@vue/apollo-util";
import { useToast } from "vue-toastification";
import {
  CREATE_TEMPLATE,
  DELETE_TEMPLATE,
  UPDATE_TEMPLATE,
} from "@/graphql/orcha/templates/mutations";
import { ApolloCache, FetchResult } from "@apollo/client";
import { insertNewEntity } from "@/helpers/cache/insertNewEntity";
import { Template, TemplatesResponse } from "@/helpers/orcha/typesDefinition";
import { Response, TemplateToDelete } from "@/helpers/typesDefinition";
import { updateTemplatesCache } from "@/helpers/orcha/templates/updateCache";
import {
  Activity,
  AddItemPayload,
  Milestone,
  templateItemType,
  UpdateItemPayload,
} from "@26lights/orcha";
import {
  CREATE_TEMPLATE_PHASE,
  UPDATE_TEMPLATE_PHASE,
  DELETE_TEMPLATE_PHASE,
} from "@/graphql/orcha/phases/mutations";
import {
  Phase,
  RemoveItemPayload,
  TemplateItemType,
} from "@26lights/orcha/dist/src/types";
import { updatePhaseCache } from "@/helpers/orcha/phases/updateCache";
import { useRoute } from "vue-router";
import { updateMilestoneCache } from "@/helpers/orcha/milestones/updateCache";
import {
  CREATE_TEMPLATE_MILESTONE,
  UPDATE_TEMPLATE_MILESTONE,
  DELETE_TEMPLATE_MILESTONE,
} from "@/graphql/orcha/milestones/mutations";
import {
  CREATE_TEMPLATE_ACTIVITY,
  UPDATE_TEMPLATE_ACTIVITY,
  DELETE_TEMPLATE_ACTIVITY,
} from "@/graphql/orcha/activities/mutations";
import { updateActivityCache } from "@/helpers/orcha/activities/updateCache";
import {
  CREATE_TEMPLATE_TASK,
  DELETE_TEMPLATE_TASK,
  UPDATE_TEMPLATE_TASK,
} from "@/graphql/orcha/tasks/mutations";
import { GET_EMPLOYEES } from "@/graphql/user/queries";
import ConfirmModal from "@/components/ConfirmModal.vue";

type MessageSchema = typeof enUK;


export default /*@__PURE__*/_defineComponent({
  __name: 'Templates',
  setup(__props) {

const { t } = useI18n<[MessageSchema], "en-UK">({
  useScope: "global",
  inheritLocale: true,
  messages: {
    "en-UK": enUK,
  },
});

const toast = useToast();

const { result: employeesResult, onError: onErrorEmployees } =
  useQuery(GET_EMPLOYEES);
const employees = computed(() => employeesResult.value?.users ?? []);
onErrorEmployees((error) => {
  logErrorMessages(error);
  toast.error(t("errors.getEmployees"));
});

const { result: templatesResult, onError: onErrorTemplates } =
  useQuery(GET_TEMPLATES);

onErrorTemplates((error) => {
  logErrorMessages(error);
  toast.error(t("errors.getTemplates"));
});

const templates = computed(() => {
  return templatesResult.value?.templates;
});

const {
  mutate: createTemplate,
  onError: onTemplateCreationError,
  onDone: onTemplateCreated,
} = useMutation(CREATE_TEMPLATE, {
  update: insertNewTemplateInCache,
});

function insertNewTemplateInCache(
  cache: ApolloCache<TemplatesResponse>,
  {
    data,
  }: Omit<FetchResult<{ createTemplate: Response<Template> }>, "context">,
) {
  const createOrderResponse = data?.createTemplate;
  if (createOrderResponse?.status === "ok") {
    updateTemplatesCache(cache, createOrderResponse.data, insertNewEntity);
  }
}

onTemplateCreationError((error) => {
  logErrorMessages(error);
  toast.error(t("errors.createTemplate"));
});

onTemplateCreated(async (result) => {
  if (result.data?.createTemplate?.status !== "ok") {
    const errorResponse = result.data?.createTemplate?.error;
    console.log(errorResponse);
    toast.error(t("errors.createTemplate", { message: errorResponse }));
  }
});

const {
  mutate: createPhaseMutation,
  onError: onPhaseCreationError,
  onDone: onPhaseCreated,
} = useMutation(CREATE_TEMPLATE_PHASE, {
  update: insertNewPhaseInCache,
});

const route = useRoute();

function insertNewPhaseInCache(
  cache: ApolloCache<TemplatesResponse>,
  {
    data,
  }: Omit<FetchResult<{ createTemplatePhase: Response<Phase> }>, "context">,
) {
  const createOrderResponse = data?.createTemplatePhase;
  if (createOrderResponse?.status === "ok") {
    const routeParams = route.params;
    const templateId = routeParams.templateId as string;
    updatePhaseCache(
      cache,
      templateId,
      createOrderResponse.data,
      insertNewEntity,
    );
  }
}

onPhaseCreationError((error) => {
  logErrorMessages(error);
  toast.error(t("errors.createPhase"));
});

onPhaseCreated(async (result) => {
  if (result.data?.createTemplatePhase?.status !== "ok") {
    const errorResponse = result.data?.createTemplatePhase?.error;
    console.log(errorResponse);
    toast.error(t("errors.createPhase", { message: errorResponse }));
  }
});

const {
  mutate: createMilestoneMutation,
  onError: onMilestoneCreationError,
  onDone: onMilestoneCreated,
} = useMutation(CREATE_TEMPLATE_MILESTONE, {
  update: insertNewMilestoneInCache,
});

function insertNewMilestoneInCache(
  cache: ApolloCache<TemplatesResponse>,
  {
    data,
  }: Omit<
    FetchResult<{ createTemplateMilestone: Response<Milestone> }>,
    "context"
  >,
) {
  const createOrderResponse = data?.createTemplateMilestone;
  if (createOrderResponse?.status === "ok") {
    const routeParams = route.params;
    const templateId = routeParams.templateId as string;
    const phaseId = routeParams.phaseId as string;
    updateMilestoneCache(
      cache,
      templateId,
      phaseId,
      createOrderResponse.data,
      insertNewEntity,
    );
  }
}

onMilestoneCreationError((error) => {
  logErrorMessages(error);
  toast.error(t("errors.createMilestone"));
});

onMilestoneCreated(async (result) => {
  if (result.data?.createTemplateMilestone?.status !== "ok") {
    const errorResponse = result.data?.createTemplateMilestone?.error;
    console.log(errorResponse);
    toast.error(t("errors.createMilestone", { message: errorResponse }));
  }
});

//////////////////////
const {
  mutate: updateTemplate,
  onError: onTemplateUpdateError,
  onDone: onTemplateUpdated,
} = useMutation(UPDATE_TEMPLATE, {
  refetchQueries: [{ query: GET_TEMPLATES }],
});

onTemplateUpdateError((error) => {
  logErrorMessages(error);
  toast.error(t("errors.updateTemplate"));
});

onTemplateUpdated(async (result) => {
  if (result.data?.updateTemplate?.status !== "ok") {
    const errorResponse = result.data?.updateTemplate?.error;
    console.log(errorResponse);
    toast.error(t("errors.updateTemplate", { message: errorResponse }));
  }
});

/////////////////

const {
  mutate: createActivityMutation,
  onError: onActivityCreationError,
  onDone: onActivityCreated,
} = useMutation(CREATE_TEMPLATE_ACTIVITY, {
  update: insertNewActivityInCache,
});

function insertNewActivityInCache(
  cache: ApolloCache<TemplatesResponse>,
  {
    data,
  }: Omit<
    FetchResult<{ createTemplateActivity: Response<Activity> }>,
    "context"
  >,
) {
  const createTemplateActivityResponse = data?.createTemplateActivity;
  if (createTemplateActivityResponse?.status === "ok") {
    const routeParams = route.params;
    const templateId = routeParams.templateId as string;
    const phaseId = routeParams.phaseId as string;
    const milestoneId = routeParams.milestoneId as string;
    updateActivityCache(
      cache,
      templateId,
      phaseId,
      milestoneId,
      createTemplateActivityResponse.data,
      insertNewEntity,
    );
  }
}

onActivityCreationError((error) => {
  logErrorMessages(error);
  toast.error(t("errors.createActivity"));
});

onActivityCreated(async (result) => {
  if (result.data?.createTemplateActivity?.status !== "ok") {
    const errorResponse = result.data?.createTemplateActivity?.error;
    console.log(errorResponse);
    toast.error(t("errors.createActivity", { message: errorResponse }));
  }
});

///////////////////
const {
  mutate: updatePhase,
  onError: onPhaseUpdateError,
  onDone: onPhaseUpdated,
} = useMutation(UPDATE_TEMPLATE_PHASE, {
  refetchQueries: [{ query: GET_TEMPLATES }],
});

onPhaseUpdateError((error) => {
  logErrorMessages(error);
  toast.error(t("errors.updatePhase"));
});

onPhaseUpdated(async (result) => {
  if (result.data?.updateTemplatePhase?.status !== "ok") {
    const errorResponse = result.data?.updateTemplatePhase?.error;
    console.log(errorResponse);
    toast.error(t("errors.updatePhase", { message: errorResponse }));
  }
});

///////////////////
const {
  mutate: updateMilestone,
  onError: onMilestoneUpdateError,
  onDone: onMilestoneUpdated,
} = useMutation(UPDATE_TEMPLATE_MILESTONE, {
  refetchQueries: [{ query: GET_TEMPLATES }],
});

onMilestoneUpdateError((error) => {
  logErrorMessages(error);
  toast.error(t("errors.updateMilestone"));
});

onMilestoneUpdated(async (result) => {
  if (result.data?.updateTemplateMilestone?.status !== "ok") {
    const errorResponse = result.data?.updateTemplateMilestone?.error;
    console.log(errorResponse);
    toast.error(t("errors.updateMilestone", { message: errorResponse }));
  }
});

///////////////////
const {
  mutate: updateActivity,
  onError: onActivityUpdateError,
  onDone: onActivityUpdated,
} = useMutation(UPDATE_TEMPLATE_ACTIVITY, {
  refetchQueries: [{ query: GET_TEMPLATES }],
});

onActivityUpdateError((error) => {
  logErrorMessages(error);
  toast.error(t("errors.updateActivity"));
});

onActivityUpdated(async (result) => {
  if (result.data?.updateTemplateActivity?.status !== "ok") {
    const errorResponse = result.data?.updateTemplateActivity?.error;
    console.log(errorResponse);
    toast.error(t("errors.updateActivity", { message: errorResponse }));
  }
});

const {
  mutate: deleteTemplatePhase,
  onError: onPhaseDeletedError,
  onDone: onPhaseDeleted,
} = useMutation(DELETE_TEMPLATE_PHASE, {
  refetchQueries: [{ query: GET_TEMPLATES }],
});

onPhaseDeletedError((error) => {
  logErrorMessages(error);
  toast.error(t("errors.deletePhase"));
});

onPhaseDeleted((result) => {
  if (result.data?.deleteTemplatePhase?.status !== "ok") {
    const errorResponse = result.data?.deleteTemplatePhase?.error;
    console.log(errorResponse);
    toast.error(t("errors.deletePhase", { message: errorResponse }));
  }
});

const {
  mutate: deleteTemplateMilestone,
  onError: onMilestoneDeletedError,
  onDone: onMilestoneDeleted,
} = useMutation(DELETE_TEMPLATE_MILESTONE, {
  refetchQueries: [{ query: GET_TEMPLATES }],
});

onMilestoneDeletedError((error) => {
  logErrorMessages(error);
  toast.error(t("errors.deleteMilestone"));
});

onMilestoneDeleted((result) => {
  if (result.data?.deleteTemplateMilestone?.status !== "ok") {
    const errorResponse = result.data?.deleteTemplateMilestone?.error;
    console.log(errorResponse);
    toast.error(t("errors.deleteMilestone", { message: errorResponse }));
  }
});

const {
  mutate: deleteTemplateActivity,
  onError: onActivityDeletedError,
  onDone: onActivityDeleted,
} = useMutation(DELETE_TEMPLATE_ACTIVITY, {
  refetchQueries: [{ query: GET_TEMPLATES }],
});

onActivityDeletedError((error) => {
  logErrorMessages(error);
  toast.error(t("errors.deleteActivity"));
});

onActivityDeleted((result) => {
  if (result.data?.deleteTemplateActivity?.status !== "ok") {
    const errorResponse = result.data?.deleteTemplateActivity?.error;
    console.log(errorResponse);
    toast.error(t("errors.deleteActivity", { message: errorResponse }));
  }
});

const {
  mutate: createTemplateTask,
  onError: onTemplateTaskCreatedError,
  onDone: onTemplateTaskCreated,
} = useMutation(CREATE_TEMPLATE_TASK, {
  refetchQueries: [{ query: GET_TEMPLATES }],
});

onTemplateTaskCreatedError((error) => {
  logErrorMessages(error);
  toast.error(t("errors.createTemplateTask"));
});

onTemplateTaskCreated((result) => {
  if (result.data?.createTemplateTask?.status !== "ok") {
    const errorResponse = result.data?.createTemplateTask?.error;
    console.log(errorResponse);
    toast.error(t("errors.createTemplateTask", { message: errorResponse }));
  }
});

const {
  mutate: deleteTemplateTask,
  onError: onTemplateTaskDeletedError,
  onDone: onTemplateTaskDeleted,
} = useMutation(DELETE_TEMPLATE_TASK, {
  refetchQueries: [{ query: GET_TEMPLATES }],
});

onTemplateTaskDeletedError((error) => {
  logErrorMessages(error);
  toast.error(t("errors.deleteTemplateTask"));
});

onTemplateTaskDeleted((result) => {
  if (result.data?.deleteTemplateTask?.status !== "ok") {
    const errorResponse = result.data?.deleteTemplateTask?.error;
    console.log(errorResponse);
    toast.error(t("errors.deleteTemplateTask", { message: errorResponse }));
  }
});

const {
  mutate: updateTemplateTask,
  onError: onTemplateTaskUpdatedError,
  onDone: onTemplateTaskUpdated,
} = useMutation(UPDATE_TEMPLATE_TASK, {
  refetchQueries: [{ query: GET_TEMPLATES }],
});

onTemplateTaskUpdatedError((error) => {
  logErrorMessages(error);
  toast.error(t("errors.updateTemplateTask"));
});

onTemplateTaskUpdated((result) => {
  if (result.data?.updateTemplateTask?.status !== "ok") {
    const errorResponse = result.data?.updateTemplateTask?.error;
    console.log(errorResponse);
    toast.error(t("errors.updateTemplateTask", { message: errorResponse }));
  }
});

const {
  mutate: deleteTemplate,
  onError: onTemplateDeletedError,
  onDone: onTemplateDeleted,
} = useMutation(DELETE_TEMPLATE, {
  refetchQueries: [{ query: GET_TEMPLATES }],
});

onTemplateDeletedError((error) => {
  logErrorMessages(error);
  toast.error(t("errors.deleteTemplate"));
});

onTemplateDeleted((result) => {
  if (result.data?.deleteTemplate?.status !== "ok") {
    const errorResponse = result.data?.deleteTemplate?.error;
    console.log(errorResponse);
    toast.error(t("errors.deleteTemplate", { message: errorResponse }));
  } else {
    toast.success(t("delete.success"));
  }
  isConfirmDeleteModalVisible.value = false;
});

////////////////////
const createItem = async (payload: AddItemPayload) => {
  const variables = { ...payload.item, templateId: payload.templateId };
  if (payload.type === templateItemType.PHASES) {
    await createPhaseMutation(variables);
  } else if (payload.type === templateItemType.MILESTONES) {
    await createMilestoneMutation(variables);
  } else if (payload.type === templateItemType.ACTIVITIES) {
    await createActivityMutation(variables);
  } else if (payload.type === templateItemType.TASKS) {
    await createTemplateTask(variables);
  }
};

const updateItem = async <T extends TemplateItemType>(
  payload: UpdateItemPayload<T>,
) => {
  if (payload.itemType === templateItemType.TEMPLATES) {
    await updateTemplate({ id: payload.id, ...payload.data });
  } else if (payload.itemType === templateItemType.PHASES) {
    await updatePhase({ id: payload.id, ...payload.data });
  } else if (payload.itemType === templateItemType.MILESTONES) {
    await updateMilestone({ id: payload.id, ...payload.data });
  } else if (payload.itemType === templateItemType.ACTIVITIES) {
    await updateActivity({ id: payload.id, ...payload.data });
  } else if (payload.itemType === templateItemType.TASKS) {
    await updateTemplateTask({ id: payload.id, ...payload.data });
  }
};

const removeItem = async <T extends TemplateItemType>(
  payload: RemoveItemPayload<T>,
) => {
  if (payload.itemType === templateItemType.TEMPLATES) {
    showConfirmDelete(payload.data);
  }
  if (payload.itemType === templateItemType.PHASES) {
    await deleteTemplatePhase(payload.data);
  } else if (payload.itemType === templateItemType.MILESTONES) {
    await deleteTemplateMilestone(payload.data);
  } else if (payload.itemType === templateItemType.ACTIVITIES) {
    await deleteTemplateActivity(payload.data);
  } else if (payload.itemType === templateItemType.TASKS) {
    await deleteTemplateTask(payload.data);
  }
};

const isConfirmDeleteModalVisible = ref(false);
const templateToDelete = ref();
const showConfirmDelete = (data: TemplateToDelete) => {
  templateToDelete.value = data;
  isConfirmDeleteModalVisible.value = true;
};
const closeConfirmDelete = () => {
  isConfirmDeleteModalVisible.value = false;
};

return (_ctx: any,_cache: any) => {
  const _component_router_view = _resolveComponent("router-view")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_component_router_view, {
      templates: templates.value,
      users: employees.value,
      onAddTemplate: _unref(createTemplate),
      onAddItem: createItem,
      onUpdateItem: updateItem,
      onRemoveItem: removeItem
    }, null, 8, ["templates", "users", "onAddTemplate"]),
    (isConfirmDeleteModalVisible.value)
      ? (_openBlock(), _createBlock(ConfirmModal, {
          key: 0,
          onClose: _cache[0] || (_cache[0] = ($event: any) => (closeConfirmDelete())),
          onNo: _cache[1] || (_cache[1] = ($event: any) => (closeConfirmDelete())),
          onYes: _cache[2] || (_cache[2] = ($event: any) => (_unref(deleteTemplate)({ id: templateToDelete.value.id }))),
          question: _unref(t)('delete.title')
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_unref(t)("delete.validate", { message: templateToDelete.value.name })), 1)
          ]),
          _: 1
        }, 8, ["question"]))
      : _createCommentVNode("", true)
  ], 64))
}
}

})